// src/components/modals/AddressBookModal.jsx
// Address book rendered INSIDE the cart modal (no separate modal): shows the
// saved-address list, an empty state with an "Add Address" button, and the
// add-address form (with Back/Continue) that appears within the same modal.
import React, { useState } from 'react';
import { showToast } from '../../utils/helpers';
import { getStoredPersonalUser } from '../../utils/personalLogin';

const AddressBookModal = ({
  initialView = 'list',
  addresses,
  selectedAddress,
  loadingAddresses,
  onSelectAddress,
  onBack,
  onCancel,
  onSaved,
  saveNewAddress
}) => {
  const [view, setView] = useState(initialView);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState(() => {
    const personalUser = getStoredPersonalUser();
    return {
      fullName: personalUser?.fullname || '',
      email: personalUser?.email || '',
      phone: personalUser?.phoneNumber || '',
      streetAddress: '',
      city: 'Lagos',
      country: 'Nigeria',
      state: ''
    };
  });

  const formatAddress = (addr) => {
    if (!addr) return '';
    return [addr.streetAddress, addr.city, addr.state, addr.country].filter(Boolean).join(', ');
  };

  const sortedAddresses = [
    ...(addresses || []).filter(a => a.isDefault),
    ...(addresses || []).filter(a => !a.isDefault)
  ];

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setAddressForm(prev => ({ ...prev, [name]: value }));
  };

  const openForm = () => {
    setAddressForm(prev => ({
      ...prev,
      state: prev.state || 'Lagos',
      country: prev.country || 'Nigeria'
    }));
    setView('form');
  };

  // Back from the form goes to the cart (first screen).
  const handleCancel = () => {
    onCancel && onCancel();
  };

  const handleContinue = async () => {
    if (!addressForm.fullName || !addressForm.email || !addressForm.streetAddress) {
      showToast('Please fill in the required fields (full name, email and street address)', 'error');
      return;
    }
    setIsSavingAddress(true);
    try {
      const saved = await saveNewAddress(addressForm);
      if (!saved) {
        showToast('Could not save delivery details. Please try again.', 'error');
        return;
      }
      showToast('Delivery details saved successfully!', 'success');
      onSaved && onSaved();
    } finally {
      setIsSavingAddress(false);
    }
  };

  if (view === 'form') {
    return (
      <>
        <div className="flex justify-between items-center p-6 border-b border-gray-200 shrink-0">
          <h2 className="text-2xl font-bold text-gray-900">Add Delivery Details</h2>
          <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 flex-1 min-h-0 overflow-y-auto">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
              <input
                type="text"
                name="fullName"
                value={addressForm.fullName}
                onChange={handleFormChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Recipient full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
              <input
                type="email"
                name="email"
                value={addressForm.email}
                onChange={handleFormChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter your email address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={addressForm.phone}
                onChange={handleFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter your phone number"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Street Address *</label>
              <input
                type="text"
                name="streetAddress"
                value={addressForm.streetAddress}
                onChange={handleFormChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="House number, street, area"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <input
                  type="text"
                  name="city"
                  value={addressForm.city}
                  onChange={handleFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="City"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                <input
                  type="text"
                  name="state"
                  value={addressForm.state}
                  onChange={handleFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="State"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                <input
                  type="text"
                  name="country"
                  value={addressForm.country}
                  onChange={handleFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Country"
                />
              </div>
            </div>

            <div className="flex space-x-4 pt-2">
              <button
                onClick={handleContinue}
                disabled={isSavingAddress}
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-full font-semibold hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 transition-colors shadow-md"
              >
                {isSavingAddress ? 'Saving...' : 'Continue'}
              </button>
              <button
                onClick={handleCancel}
                className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Address book list view (empty state when no addresses exist)
  return (
    <>
      <div className="flex justify-between items-center p-6 border-b border-gray-200 shrink-0">
        <h2 className="text-2xl font-bold text-gray-900">Select Delivery Details</h2>
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-full px-4 py-2 hover:bg-gray-100 hover:text-gray-800 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
      </div>

      <div className="p-6 flex-1 min-h-0 overflow-y-auto">
        {loadingAddresses && sortedAddresses.length === 0 ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your addresses...</p>
          </div>
        ) : sortedAddresses.length === 0 ? (
          <div className="text-center py-10 border-2 border-dashed border-gray-300 rounded-2xl">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="font-medium text-gray-700">No saved address yet</p>
            <p className="text-sm text-gray-500 mt-1">Add your delivery details to continue.</p>
            <button
              onClick={openForm}
              className="mt-5 inline-flex items-center bg-blue-600 text-white px-5 py-2.5 rounded-full font-medium hover:bg-blue-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Add Delivery Details
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedAddresses.map(address => {
              const isSelected = selectedAddress && selectedAddress.id === address.id;
              return (
                <div
                  key={address.id}
                  onClick={() => onSelectAddress && onSelectAddress(address)}
                  className={`border-2 rounded-2xl p-4 cursor-pointer transition-all duration-300 ${
                    isSelected
                      ? 'border-blue-600 bg-blue-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-blue-500'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                      }`}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                          </svg>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h4 className="font-medium text-gray-900">{address.label || 'Address'}</h4>
                          {address.isDefault && (
                            <span className="text-xs font-medium bg-blue-600 text-white px-2 py-0.5 rounded-full">Default</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{formatAddress(address)}</p>
                        {address.fullName && (
                          <p className="text-sm text-gray-500 mt-1">
                            {address.fullName}{address.email ? ` • ${address.email}` : ''}{address.phone ? ` • ${address.phone}` : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {sortedAddresses.length > 0 && (
          <button
            onClick={openForm}
            className="mt-6 w-full flex items-center justify-center border-2 border-dashed border-gray-300 rounded-2xl py-3 text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Delivery Details
          </button>
        )}
      </div>
    </>
  );
};

export default AddressBookModal;
